/**
 * Client-onboarding checklist — the contract shared by the API
 * (`routes/client-onboarding`), the field-agent tool (`/client-onboarding`)
 * and the Control Center read-only views.
 *
 * One catalogue (`./catalogue.ts`), one progress engine (`./progress.ts`), so
 * the number an agent sees on their phone is the number Control Center sees
 * and the number the API persists. Before 2026-10-06 the API carried its own
 * copy of the step ids and the web its own progress formula, and the two had
 * already drifted (a "62 steps ready" toast on an 87-step list).
 */

/** Where a step stands for THIS school. */
export const ONBOARDING_STEP_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  /** Waiting on the school (a document, a decision, a person). Needs a note. */
  "blocked",
  /** Not applicable for this school. Only optional steps, or steps of a module the school does not use. */
  "skipped",
] as const;
export type OnboardingStepStatus = (typeof ONBOARDING_STEP_STATUSES)[number];

/**
 * Optional modules a school may or may not use. Switching one off at tracker
 * level marks every step of that module `skipped`, so the checklist adapts to
 * the school instead of asking an agent to "skip" twenty rows by hand.
 */
export const ONBOARDING_MODULES = [
  "transport",
  "gps",
  "hostel",
  "admissions",
  "onlinePayments",
  "website",
  "idCards",
] as const;
export type OnboardingModuleKey = (typeof ONBOARDING_MODULES)[number];
export type OnboardingModuleFlags = Record<OnboardingModuleKey, boolean>;

export const ONBOARDING_MODULE_LABELS: Record<OnboardingModuleKey, { label: string; hint: string }> = {
  transport: { label: "School transport", hint: "Buses / vans with routes and stops" },
  gps: { label: "GPS live tracking", hint: "GPS devices fitted in the vehicles" },
  hostel: { label: "Hostel / boarding", hint: "Boarders living on campus" },
  admissions: { label: "Online admissions", hint: "Applications through the CampusHub admissions pipeline" },
  onlinePayments: { label: "Online fee payment", hint: "Parents paying fees inside the app (Cashfree)" },
  website: { label: "School website", hint: "A public site on <school>.bytego.ai" },
  idCards: { label: "ID cards & documents", hint: "Student ID cards and certificates from Studio" },
};

export const DEFAULT_ONBOARDING_MODULES: OnboardingModuleFlags = {
  transport: true,
  gps: false,
  hostel: false,
  admissions: false,
  onlinePayments: true,
  website: true,
  idCards: true,
};

/** Who physically does the step. */
export type OnboardingOwner = "agent" | "school" | "bytego";

export const ONBOARDING_OWNER_LABELS: Record<OnboardingOwner, string> = {
  agent: "Field agent",
  school: "School staff",
  bytego: "ByteGo ops",
};

export interface OnboardingStep {
  /** Stable id — persisted per tracker. Never rename; add new ids instead. */
  id: string;
  title: string;
  /** One line under the title: what "done" means in plain words. */
  summary: string;
  owner: OnboardingOwner;
  /**
   * Required steps cannot be skipped (unless their module is off). An
   * optional step can be marked "Not applicable" with a reason.
   */
  required: boolean;
  /** Present when the step only applies if the school uses this module. */
  module?: OnboardingModuleKey;
  /**
   * In-app route the agent opens to do the step (web path, no host). Shown as
   * an "Open in CampusHub" link; omitted for off-app steps (a phone call, a
   * printed sheet).
   */
  route?: string;
  /** Realistic hands-on minutes for a typical 500-student school. */
  minutes: number;
  /** Steps that should be done first. Surfaced as a warning, never enforced. */
  dependsOn?: string[];
  /** How to do it — ordered, imperative, code-truthful. */
  how: string[];
  /** Definition of done — what the agent checks before ticking the box. */
  verify: string[];
  /** Things that have gone wrong at other schools. */
  pitfalls?: string[];
  /** Field wisdom. */
  tips?: string[];
}

export interface OnboardingSection {
  id: string;
  title: string;
  steps: OnboardingStep[];
}

export type OnboardingTone =
  | "blue"
  | "violet"
  | "green"
  | "amber"
  | "rose"
  | "red"
  | "sky"
  | "teal"
  | "pink"
  | "indigo"
  | "orange"
  | "slate";

export interface OnboardingPhase {
  id: string;
  /** Short, e.g. "Students & parents". */
  title: string;
  /** The outcome in one line, e.g. "Every child is in, every parent can log in." */
  tagline: string;
  description: string;
  tone: OnboardingTone;
  /** Whole phase is only applicable when the school uses this module. */
  module?: OnboardingModuleKey;
  sections: OnboardingSection[];
}

/** Persisted per tracker, one row per catalogue step. */
export interface OnboardingStepState {
  id: string;
  status: OnboardingStepStatus;
  /** Agent's note. Mandatory (non-empty) for `blocked` and `skipped`. */
  comment: string | null;
  /** ISO timestamp set when the step was last marked completed. */
  completedAt: string | null;
  /** ISO timestamp of the last change to this row. Absent on rows written before 2026-10-06. */
  updatedAt?: string | null;
}

export const ONBOARDING_TRACKER_STATUSES = ["NOT_STARTED", "IN_PROGRESS", "COMPLETED"] as const;
export type OnboardingTrackerStatus = (typeof ONBOARDING_TRACKER_STATUSES)[number];

export interface OnboardingSummary {
  /** Steps in the catalogue. */
  total: number;
  /** Steps that count for this school (total − skipped). */
  applicable: number;
  completed: number;
  inProgress: number;
  blocked: number;
  skipped: number;
  pending: number;
  /** completed ÷ applicable, 0–100, rounded. */
  progress: number;
  status: OnboardingTrackerStatus;
  /** Sum of `minutes` over applicable, not-yet-completed steps. */
  minutesLeft: number;
}

export interface OnboardingPhaseSummary extends OnboardingSummary {
  phaseId: string;
}
